import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Autocomplete, Card, Divider, Fade, Grid, Icon, InputLabel, Modal, TextField, Tooltip, Menu, MenuItem, Slide } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import AgregarProductoConsumoInterno from './components/AgregarProductoConsumoInterno';
import ListDetallesConsumoInterno from './components/ListDetallesConsumoInterno';
import SoftBadge from 'components/SoftBadge';
import dayjs from 'dayjs';
import { getUproColor } from 'utils/colors';

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "90%", sm: "70%", xxl: "60%" },
    minWidth: "45%",
    minHeight: "60%",
    overflowY: "auto",
    p: 3,
};

function ModalConsumoInterno({ open, onClose }) {
    const [search, setSearch] = useState("");
    const [pedido, setPedido] = useState([]);
    const [dataGeneral, setDataGeneral] = useState(null);
    const [empleadoSelected, setEmpleadoSelected] = useState(null);
    const [success, setSuccess] = useState(false);
    const today = useRef(null);
    today.current = dayjs().format('YYYY-MM-DD');

    function transformarPedidos(pedidoData) {
        const pedido = pedidoData?.pedidosInterno?.[0];
        if (!pedido) return [];

        setDataGeneral({
            id: pedido.id,
            total: pedido.total,
            fechaPedido: pedido.fechaPedido,
            pagado: pedido.pagado,
            comentario: pedido.comentario
        });

        const empleadosMap = new Map();
        for (const detalle of pedido.detalle ?? []) {
            const { empleado, consumo, id } = detalle;
            if (!empleado) continue;

            const idEmpleado = empleado.id;

            if (!empleadosMap.has(idEmpleado)) {
                empleadosMap.set(idEmpleado, {
                    idDetalle: id,
                    idEmpleado,
                    nombre: empleado.nombre,
                    apellido: empleado.apellido,
                    deleted: 0,
                    detalles: [],
                });
            }

            const empleadoObj = empleadosMap.get(idEmpleado);

            for (const item of consumo ?? []) {
                const detalleTransformado = {
                    id: item.id,
                    cantidad: item.cantidad,
                    estado: item.estado,
                    precio: item.precio ?? 0,
                    precioEditado: item?.precioEditado ?? null,
                    producto: {
                        id: item.producto?.id ?? null,
                        nombre: item.producto?.nombre ?? "Producto desconocido",
                        esElaborado: item.esElaborado ?? false,
                        precioCarta: item?.producto?.precioCarta
                    },
                };

                empleadoObj.detalles.push(detalleTransformado);
            }
        }

        return Array.from(empleadosMap.values());
    }

    const { data: empleadosData } = useQuery(
        gql`
          query getEmpleados($filter: JSON) {
            usuarios(filter: $filter) {
              id
              nombre
              apellido
            }
          }
        `,
        {
            variables: {
                filter: { tipoUser: "Empleado" },
            },
            fetchPolicy: "network-only",
        }
    );

    const { data: pedidoData, loading: loadingConsumo, refetch: refetchConsumo } = useQuery(
        gql`
        query consumoInterno($filter: JSON) {
          pedidosInterno(filter: $filter) {
            id
            total
            fechaPedido
            pagado
            detalle {
              id
              empleado {
                id
                nombre
                apellido
              }
              consumo {
                id
                cantidad
                comentario
                costo
                esElaborado
                estado
                precio
                precioEditado
                producto {
                    id
                    esElaborado
                    nombre
                    precioCarta
                }
              }
            }
            comentario
          }
        }
      `,
        {
            variables: {
                filter: { fechaPedido: today.current },
            },
            skip: !open || today.current === null,
            fetchPolicy: "no-cache",
            cachePolicy: "no-cache",
        }
    );

    const empleados = (empleadosData?.usuarios || []).map(emp => ({ idEmpleado: emp.id, nombre: emp.nombre, apellido: emp.apellido, detalles: [] }));

    const handleSearchChange = (event, value) => {
        setSearch(value);
    };

    const handleSelectEmpleado = (event, newValue) => {
        setEmpleadoSelected(newValue);
        setPedido(prev => {
            if (newValue && !prev.some(emp => emp.deleted === 0 && emp.idEmpleado === newValue.idEmpleado)) {
                return [
                    ...prev,
                    {
                        idEmpleado: newValue.idEmpleado,
                        nombre: newValue.nombre,
                        apellido: newValue.apellido,
                        deleted: 0,
                        detalles: []
                    }
                ];
            }
            return prev;
        });
    };

    const handleSuccess = () => {
        setSuccess(true);
        setPedido([]);
        setDataGeneral(null);
        setEmpleadoSelected(null);
        refetchConsumo();
        setTimeout(() => {onClose(); setSuccess(false)}, 3000);
    }

    useEffect(() => {
        if (open) {
            setEmpleadoSelected(null);
            if (pedidoData?.pedidosInterno.length > 0) {
                const empleadosFormateados = transformarPedidos(pedidoData);
                setPedido(empleadosFormateados || [])
            }
        }
    }, [open, pedidoData?.pedidosInterno]);

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={open}
            onClose={onClose}
            closeAfterTransition
        >
            <Fade in={open}>
                <Card sx={style}>
                    <Slide in={loadingConsumo} direction="up" mountOnEnter unmountOnExit>
                        <SoftBox
                            bgColor={getUproColor("uproGray")}
                            p={3}
                            py={"5rem"}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                            sx={{
                                width: "100%",
                                height: "100%",
                                minHeight: "100%",
                                minWidth: "100%",
                                position: "absolute",
                                top: 0,
                                left: 0,
                            }}
                        >
                            <Icon
                                fontSize="large"
                                sx={{
                                    color: "#fff",
                                    animation: "shakes 1s infinite",
                                    "@keyframes shakes": {
                                        "0%": {
                                            transform: "rotate(0deg)",
                                        },
                                        "25%": {
                                            transform: "rotate(10deg)",
                                        },
                                        "50%": {
                                            transform: "rotate(-20deg)",
                                        },
                                        "75%": {
                                            transform: "rotate(15deg)",
                                        },
                                        "100%": {
                                            transform: "rotate(-5deg)",
                                        },
                                    },
                                }}
                            >
                                hourglass_empty
                            </Icon>
                            <SoftTypography variant="h5" color="white" fontWeight="bold" mt={2}>
                                Cargando datos
                            </SoftTypography>
                        </SoftBox>
                    </Slide>
                    <Slide in={success} direction="up" mountOnEnter unmountOnExit>
                        <SoftBox
                            bgColor={getUproColor("uproGreen")}
                            p={3}
                            py={"5rem"}
                            display="flex"
                            flexDirection="column"
                            justifyContent="center"
                            alignItems="center"
                            textAlign="center"
                            sx={{
                                width: "100%",
                                height: "100%",
                                minHeight: "100%",
                                minWidth: "100%",
                                position: "absolute",
                                top: 0,
                                left: 0,
                            }}
                        >
                            <Icon
                                fontSize="large"
                                sx={{
                                    color: "#fff",
                                    animation: "shakes 1s infinite",
                                    "@keyframes shakes": {
                                        "0%": {
                                            transform: "rotate(0deg)",
                                        },
                                        "25%": {
                                            transform: "rotate(10deg)",
                                        },
                                        "50%": {
                                            transform: "rotate(-20deg)",
                                        },
                                        "75%": {
                                            transform: "rotate(15deg)",
                                        },
                                        "100%": {
                                            transform: "rotate(-5deg)",
                                        },
                                    },
                                }}
                            >
                                check
                            </Icon>
                            <SoftTypography variant="h5" color="white" fontWeight="bold" mt={2}>
                                Mesa guardada!
                            </SoftTypography>
                        </SoftBox>
                    </Slide>
                    {!loadingConsumo && !success && (<>
                        <SoftBox display="flex" justifyContent="space-between">
                            <SoftTypography variant="h6">Consumo de personal<SoftTypography variant="h6" color="secondary" pl={.5} fontWeight="bold">{dayjs().format("DD/MM/YYYY")}</SoftTypography></SoftTypography>
                            <Tooltip title="Cerrar" placement="top">
                                <Icon fontSize="medium" onClick={onClose} sx={{ cursor: "pointer" }}>
                                    close
                                </Icon>
                            </Tooltip>
                        </SoftBox>
                        <Grid container my={2}>
                            <Grid item xs={12}>
                                <InputLabel htmlFor="empleado">
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        Empleado
                                    </SoftTypography>
                                </InputLabel>
                                <Autocomplete
                                    clearOnEscape
                                    options={empleados}
                                    value={empleadoSelected}
                                    loading={!empleadosData}
                                    loadingText="Cargando empleados..."
                                    noOptionsText={
                                        search.length > 1 ? "No se encontraron resultados" : "Escriba para buscar"
                                    }
                                    onInputChange={handleSearchChange}
                                    getOptionLabel={(option) =>
                                        `${option.nombre} ${option.apellido}`
                                    }
                                    filterOptions={(options, params) => {
                                        const input = params.inputValue.toLowerCase().trim();
                                        if (!input) return options;

                                        return options.filter((option) => {
                                            const nombre = option.nombre?.toLowerCase() || "";
                                            const apellido = option.apellido?.toLowerCase() || "";
                                            const nombreApellido = `${nombre} ${apellido}`;
                                            const apellidoNombre = `${apellido} ${nombre}`;
                                            return (
                                                nombre.includes(input) ||
                                                apellido.includes(input) ||
                                                nombreApellido.includes(input) ||
                                                apellidoNombre.includes(input)
                                            );
                                        });
                                    }}
                                    onChange={handleSelectEmpleado}
                                    isOptionDisabled={(option) => {
                                        return pedido.some(emp => emp.idEmpleado === option.id);
                                    }}
                                    renderOption={(props, option) => {
                                        return (
                                            <li {...props} style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                                <span>{`${option.nombre} ${option.apellido}`}</span>
                                                {pedido.some(emp => emp.deleted === 0 && emp.idEmpleado === option.idEmpleado) ? (
                                                    <SoftBadge
                                                        badgeContent='Seleccionar'
                                                        variant={'color'}
                                                    />
                                                ) : (
                                                    <SoftBadge
                                                        badgeContent='Agregar'
                                                        variant={'colorGradient'}
                                                    />
                                                )}
                                            </li>
                                        )
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Buscar empleado..."
                                            slotprops={{
                                                input: {
                                                    ...params.InputProps,
                                                    type: "search",
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        <AgregarProductoConsumoInterno
                            pedido={empleadoSelected ? pedido.find(emp => emp.deleted === 0 && emp?.idEmpleado === empleadoSelected?.idEmpleado) || { detalles: [] } : { detalles: [] }}
                            setPedido={(updatedPedido) => {
                                setPedido(prev => prev.map(emp => {
                                    return emp.deleted === 0 && emp.idEmpleado === empleadoSelected?.idEmpleado ? updatedPedido : emp
                                }
                                ));
                            }}
                            open={open}
                            disabled={(pedido || []).filter(emp => emp.deleted === 0).length === 0}
                        />
                        <Divider />
                        <SoftBox display="flex" flexDirection="column" gap={2}>
                            <ListDetallesConsumoInterno pedido={pedido} setEmpleadoSelected={setEmpleadoSelected} setPedido={setPedido} handleSuccess={handleSuccess} dataGeneral={dataGeneral} />
                        </SoftBox>
                    </>)}
                </Card>
            </Fade>
        </Modal>
    );
}

export default ModalConsumoInterno;